import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MaskedInput from "react-text-mask";
import { connect } from "react-redux";
import { ApplicationStore } from "src/store";
import { getEmail, getMobilePhone } from "src/store/selectors/person";
import styled from "styled-components";
import LoadingBar from "Shared/LoadingBar";
import { cleanPhone, validateEmail, validatePhone } from "Shared/Form/validation";
import { patchTo } from "src/store/dataService/routes/shared/osfetch";
import { getAccountCountry } from "src/store/selectors/account";
import { logError } from "src/utils/logger";

interface Props {
    loaded: boolean;
}

const NoMessagesDiv = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    justify-content: space-between;
    padding-left: 24px;
    padding-right: 24px;
    padding-top: 1rem;
    padding-bottom: 0.5rem;
    flex-shrink: 2;
    height: 250px;
`;

const DescriptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding-top: 1rem;
`;

const Heading = styled.div`
    font-weight: 500;
    font-size: 1.2rem;
    padding-bottom: 1rem;
`;

const LoadingContainer = styled.div`
    margin-left: 24px;
    margin-right: 24px;
    text-align: center;
    font-weight: 500;
    display: flex;
    justify-content: space-evenly;
    flex-direction: column;
    height: 75px;
    margin-top: 100px;
`;

const ContactContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const ContactLabel = styled.div`
    display: block;
    font-size: 16px;
    font-weight: bold;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
`;

const ContactRadioLabel = styled.label`
    margin-right: 0.5em;
`;

const ContactInput = styled(MaskedInput)`
    height: 40px;
    border: 1px solid #ccc;
    border-radius: 1px;
    font-size: 15px;
    padding: 5px 0 6px 10px;
    line-height: 2em;
    width: 100%;
    background-color: #fff;
`;

const ContactInputEmail = styled.input`
    height: 40px;
    border: 1px solid #ccc;
    border-radius: 1px;
    font-size: 15px;
    padding: 5px 0 6px 10px;
    line-height: 2em;
    width: 100%;
    background-color: #fff;
`;

export const NoMessages = ({
    uuid,
    region,
    dealershipName,
    loaded,
    methodOfContact,
    emailAddress,
    phoneNumber,
}: Props & ReturnType<typeof mapStateToProps>) => {
    const { t } = useTranslation();

    const [contactMethod, setContactMethod] = useState(methodOfContact);
    const [phone, setPhone] = useState(phoneNumber ?? "");
    const [email, setEmail] = useState(emailAddress ?? "");

    const handleBlur = () => {
        if (contactMethod === "sms") {
            if (methodOfContact === "sms" && cleanPhone(phone) === phoneNumber) {
                return;
            }

            if (phone && validatePhone(phone)) {
                patchTo(`/deals/${uuid}/person`, {
                    mobile_phone: cleanPhone(phone),
                    preferences: {
                        method_of_contact: "sms",
                        ...(region === "CA" && { opt_in_marketing: true }),
                    },
                }).catch((e) => logError(e));
            }

            return;
        }

        if (methodOfContact === "email" && emailAddress === email) {
            return;
        }

        if (email && validateEmail(email)) {
            patchTo(`/deals/${uuid}/person`, {
                email: email.trim(),
                preferences: {
                    method_of_contact: "email",
                    ...(region === "CA" && { opt_in_marketing: true }),
                },
            }).catch((e) => logError(e));
        }
    };

    if (!loaded) {
        return (
            <LoadingContainer>
                <LoadingBar />
                {t("MessageCenter.NoMessages.loading")}
            </LoadingContainer>
        );
    }

    return (
        <NoMessagesDiv>
            <DescriptionContainer>
                <Heading id={"NoMessages"}>{`${t("MessageCenter.NoMessages.header")} ${dealershipName}`}</Heading>
                <div>{t("MessageCenter.NoMessages.description")}</div>
            </DescriptionContainer>
            <ContactContainer>
                <ContactLabel>{t("MessageCenter.contactSection.Label")}</ContactLabel>
                <div style={{ width: "100%" }}>
                    <ContactRadioLabel>
                        <input
                            name={"contact-method"}
                            type="radio"
                            checked={contactMethod === "sms"}
                            value={"sms"}
                            onChange={() => setContactMethod("sms")}
                        />
                        {t("MessageCenter.phone.Label")}
                    </ContactRadioLabel>
                    <ContactRadioLabel>
                        <input
                            name={"contact-method"}
                            type="radio"
                            checked={contactMethod === "email"}
                            value={"email"}
                            onChange={() => setContactMethod("email")}
                        />
                        {t("MessageCenter.email.Label")}
                    </ContactRadioLabel>
                    <div key={contactMethod} style={{ width: "80%", marginTop: "0.5em" }}>
                        {contactMethod === "sms" ? (
                            <ContactInput
                                key={contactMethod}
                                mask={["(", /\d/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/]}
                                guide={false}
                                placeholder={t("MessageCenter.phone.placeholder")}
                                type="tel"
                                name="phone"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                onBlur={handleBlur}
                            />
                        ) : (
                            <ContactInputEmail
                                key={contactMethod}
                                type="email"
                                name="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t("MessageCenter.email.placeholder")}
                                onBlur={handleBlur}
                            />
                        )}
                    </div>
                </div>
            </ContactContainer>
        </NoMessagesDiv>
    );
};

const mapStateToProps = (state: ApplicationStore) => ({
    uuid: state.deal.uuid,
    dealershipName: state.account.name,
    methodOfContact: state.deal.person?.preferences?.method_of_contact ?? "sms",
    phoneNumber: getMobilePhone(state),
    emailAddress: getEmail(state),
    region: getAccountCountry(state),
});

export default connect(mapStateToProps)(NoMessages);
